import React, { ReactNode } from "react";
import Button from "../button/Button";
import "./StepNavigation.scss";

interface StepNavigationProps {
	currentStep: number;
	totalSteps: number;
	onStepClick?: (step: number) => void;
	stepTitles: string[];
	visitedSteps?: Set<number>;
	hasStepData?: (step: number) => boolean;
	getStepCompletionStatus?: (
		step: number
	) => "complete" | "partial" | "incomplete";
	onNext?: () => void;
	onPrev?: () => void;
	onSubmit?: () => void;
	onSecondarySubmit?: () => void;
	nextLabel?: string;
	prevLabel?: string;
	submitLabel?: string;
	secondarySubmitLabel?: string;
	showNavigationButtons?: boolean;
	children?: ReactNode;
}

const StepNavigation: React.FC<StepNavigationProps> = ({
	currentStep,
	totalSteps,
	onStepClick,
	stepTitles,
	visitedSteps,
	hasStepData,
	getStepCompletionStatus,
	onNext,
	onPrev,
	onSubmit,
	onSecondarySubmit,
	nextLabel = "Next",
	prevLabel = "Previous",
	submitLabel = "Submit",
	secondarySubmitLabel = "Save as Draft",
	showNavigationButtons = true,
	children,
}) => {
	return (
		<div className="step-navigation-container">
			{/* Stepper indicators */}
			<div className="step-navigation">
				{Array.from({ length: totalSteps }, (_, index) => {
					const stepNumber = index + 1;
					const isActive = stepNumber === currentStep;
					const completionStatus =
						getStepCompletionStatus?.(stepNumber) || "incomplete";
					const hasData = hasStepData?.(stepNumber) || false;
					const isVisited = visitedSteps?.has(stepNumber) || false;
					const isClickable =
						onStepClick && (isVisited || hasData || stepNumber <= currentStep);

					// Determine CSS classes based on completion status and current step
					const isCompleted = completionStatus === "complete" && !isActive;
					const isPartial = completionStatus === "partial" && !isActive;

					return (
						<div
							key={stepNumber}
							className={`step-item ${isActive ? "active-step" : ""} ${
								isCompleted ? "completed" : ""
							} ${isPartial ? "partial" : ""} ${
								isClickable ? "clickable" : ""
							}`}
							onClick={isClickable ? () => onStepClick(stepNumber) : undefined}
						>
							<div className="step-circle">
								{isCompleted ? "✓" : stepNumber}
							</div>
							<div className="step-title">{stepTitles[index]}</div>
							{stepNumber < totalSteps && <div className="step-connector" />}
						</div>
					);
				})}
			</div>

			{/* Content (Outlet) */}
			<div className="step-content">{children}</div>

			{/* Navigation buttons */}
			{showNavigationButtons && (
				<div className="step-navigation-buttons">
					{currentStep < totalSteps && (
						<>
							{currentStep > 1 && onPrev ? (
								<Button
									type="button"
									className="btn btn-outline-light"
									action={onPrev}
								>
									{prevLabel}
								</Button>
							) : (
								<div></div>
							)}

							{onNext && (
								<Button
									type="button"
									className="btn btn-primary"
									action={onNext}
								>
									{nextLabel}
								</Button>
							)}
						</>
					)}

					{currentStep === totalSteps && (
						<>
							<div className="step-navigation-buttons__row">
								{onSecondarySubmit && (
									<Button
										type="button"
										className="btn btn-outline-light"
										action={onSecondarySubmit}
									>
										{secondarySubmitLabel}
									</Button>
								)}
								{onSubmit && (
									<Button
										type="button"
										className="btn btn-primary"
										action={onSubmit}
									>
										{submitLabel}
									</Button>
								)}
							</div>

							{onPrev && (
								<Button
									type="button"
									className="btn btn-outline-light"
									action={onPrev}
								>
									{prevLabel}
								</Button>
							)}
						</>
					)}
				</div>
			)}
		</div>
	);
};

export default StepNavigation;
